import React, { useContext, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { findAppForPath, hasUnseenReleaseNotes, useManualIndex } from '../../manual';
import '../../styles/Layout.css';

// `fullBleed` drops the content padding and scroll gutter so an embedded
// app (e.g. the LSTM Trading Lab iframe) can fill the whole content area.
const Layout = ({ children, fullBleed = false }) => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  // Loaded once per page load from the (login-only) manual API. Until it
  // arrives, or if it fails, the "?" button and the unread dot just don't show.
  const { index: manualIndex } = useManualIndex();
  // Re-evaluated on every navigation, so the dot clears after visiting What's New.
  const currentApp = findAppForPath(manualIndex, location.pathname);
  const unseenReleaseNotes = hasUnseenReleaseNotes(manualIndex);

  // Initialize sidebar state from localStorage
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved !== null ? JSON.parse(saved) : false;
  });

  // Save sidebar state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  const handleLogout = () => {
    auth.logout();
    navigate('/login');
  };

  return (
    <div className="layout-container">
      {/* Top Navigation */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-dark border-bottom border-secondary">
        <div className="container-fluid">
          <Link className="navbar-brand" to="/">
            <i className="bi bi-arrow-repeat me-2"></i>
            Rebalancer 2.0
          </Link>
          <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav">
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav ms-auto align-items-lg-center">
              {currentApp && (
                <li className="nav-item me-3">
                  <Link
                    className="nav-link text-light"
                    to={`/help/${currentApp.id}`}
                    title={`Help for ${currentApp.title}`}
                    aria-label={`Help for ${currentApp.title}`}
                  >
                    <i className="bi bi-question-circle"></i>
                  </Link>
                </li>
              )}
              <li className="nav-item dropdown">
                <button
                  className="nav-link dropdown-toggle btn btn-link p-0 text-light"
                  id="userDropdown"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  style={{ textDecoration: 'none' }}
                >
                  <i className="bi bi-person-circle me-1"></i>
                  {auth.user?.full_name || auth.user?.email?.split('@')[0] || auth.user?.username || 'User'}
                </button>
                <ul className="dropdown-menu dropdown-menu-dark dropdown-menu-end">
                  <li><Link className="dropdown-item" to="/profile"><i className="bi bi-person me-2"></i>Profile</Link></li>
                  <li><Link className="dropdown-item" to="/user-management"><i className="bi bi-person-gear me-2"></i>Account</Link></li>
                  <li><Link className="dropdown-item" to="/settings"><i className="bi bi-gear me-2"></i>Settings</Link></li>
                  <li><hr className="dropdown-divider border-secondary" /></li>
                  <li><button className="dropdown-item text-danger" onClick={handleLogout}><i className="bi bi-box-arrow-right me-2"></i>Logout</button></li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      <div className="content-wrapper">
        {/* Sidebar Navigation */}
        <div className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
          <button
            className="btn btn-link text-light border-0 d-flex align-items-center justify-content-center py-2"
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <i className={`bi bi-chevron-${isSidebarCollapsed ? 'right' : 'left'}`}></i>
          </button>
          <nav className="nav flex-column py-3">
            <Link className="nav-link" to="/">
              <i className="bi bi-speedometer2 me-2"></i>
              {!isSidebarCollapsed && <span>Dashboard</span>}
            </Link>

            <Link className="nav-link" to="/activity">
              <i className="bi bi-activity me-2"></i>
              {!isSidebarCollapsed && <span>Activity</span>}
            </Link>

            {/* Expense Reports - every signed-in employee */}
            {auth.user && (
              <Link className="nav-link" to="/expense-reports">
                <i className="bi bi-receipt me-2"></i>
                {!isSidebarCollapsed && <span>Expense Reports</span>}
              </Link>
            )}

            {/* Sharepoint Upload - requires 'sharepoint_upload' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('sharepoint_upload') && (
              <Link className="nav-link" to="/sharepoint-upload">
                <i className="bi bi-folder2-open me-2"></i>
                {!isSidebarCollapsed && <span>Sharepoint Upload</span>}
              </Link>
            )}

            {/* IPS Scanner - requires 'ips_scanner' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('ips_scanner') && (
              <Link className="nav-link" to="/ips-scanner">
                <i className="bi bi-file-earmark-check me-2"></i>
                {!isSidebarCollapsed && <span>IPS Scanner</span>}
              </Link>
            )}

            {/* eMoney Build (the Document Data page) - requires 'document_data' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('document_data') && (
              <Link className="nav-link" to="/document-data">
                <i className="bi bi-table me-2"></i>
                {!isSidebarCollapsed && <span>eMoney Build</span>}
              </Link>
            )}

            {auth.user && auth.hasPermission && auth.hasPermission('rebalancer') && (

              <Link className="nav-link" to="/view-files">
                <i className="bi bi-file-earmark-text me-2"></i>
                {!isSidebarCollapsed && <span>View Files</span>}
              </Link>
            )}

            {/* Rebalancer - requires 'rebalancer' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('rebalancer') && (
              <Link className="nav-link" to="/rebalance">
                <i className="bi bi-arrow-repeat me-2"></i>
                {!isSidebarCollapsed && <span>Rebalance</span>}
              </Link>
            )}

            {/* Rebalancer Dashboard - requires 'rebalancer' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('rebalancer') && (
              <Link className="nav-link" to="/rebalancer-dashboard">
                <i className="bi bi-graph-up me-2"></i>
                {!isSidebarCollapsed && <span>Rebalancer Dashboard</span>}
              </Link>
            )}

            {/* Cash Reserves Monitor - requires 'cash_reserves_monitor' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('cash_reserves_monitor') && (
              <Link className="nav-link" to="/cash-reserves">
                <i className="bi bi-piggy-bank me-2"></i>
                {!isSidebarCollapsed && <span>Cash Reserves</span>}
              </Link>
            )}

            {/* Transaction History - requires 'transaction_history' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('transaction_history') && (
              <Link className="nav-link" to="/transaction-history">
                <i className="bi bi-clock-history me-2"></i>
                {!isSidebarCollapsed && <span>Transaction History</span>}
              </Link>
            )}

            {/* Directed Trade - requires 'rebalancer' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('rebalancer') && (
              <Link className="nav-link" to="/directed-trade">
                <i className="bi bi-cash-coin me-2"></i>
                {!isSidebarCollapsed && <span>Directed Trade</span>}
              </Link>
            )}

            {/* Trade Tracker - requires 'trade_tracker' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('trade_tracker') && (
              <Link className="nav-link" to="/trade-tracker">
                <i className="bi bi-journal-check me-2"></i>
                {!isSidebarCollapsed && <span>Trade Tracker</span>}
              </Link>
            )}

            {/* LSTM Trading Lab - requires 'lstm' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('lstm') && (
              <Link className="nav-link" to="/lstm" aria-label="LSTM Trading Lab" title="LSTM Trading Lab">
                <i className="bi bi-graph-up-arrow me-2"></i>
                {!isSidebarCollapsed && <span>LSTM Trading Lab</span>}
              </Link>
            )}

            {/* CC Management - requires 'cc_management' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('cc_management') && (
              <Link className="nav-link" to="/covered-calls">
                <i className="bi bi-telephone me-2"></i>
                {!isSidebarCollapsed && <span>CC Management</span>}
              </Link>
            )}

            {/* Document Chat - requires 'doc_chat' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('doc_chat') && (
              <Link className="nav-link" to="/doc-chat">
                <i className="bi bi-file-earmark-text me-2"></i>
                {!isSidebarCollapsed && <span>Document Chat</span>}
              </Link>
            )}

            {/* Client Helper - requires 'client_helper' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('client_helper') && (
              <Link className="nav-link" to="/client-helper">
                <i className="bi bi-robot me-2"></i>
                {!isSidebarCollapsed && <span>Client Helper</span>}
              </Link>
            )}

            {/* Vector Inspector - requires 'vector_inspector' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('vector_inspector') && (
              <Link className="nav-link" to="/vector-inspector">
                <i className="bi bi-search me-2"></i>
                {!isSidebarCollapsed && <span>Vector Inspector</span>}
              </Link>
            )}

            {/* Chat - requires 'chat' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('chat') && (
              <Link className="nav-link" to="/chat">
                <i className="bi bi-chat-dots me-2"></i>
                {!isSidebarCollapsed && <span>Deep Research</span>}
              </Link>
            )}

            {/* Client Admin - requires 'client_admin' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('client_admin') && (
              <Link className="nav-link" to="/client-admin">
                <i className="bi bi-shield-check me-2"></i>
                {!isSidebarCollapsed && <span>Client Admin</span>}
              </Link>
            )}

            {/* Mass Text - requires 'mass_text' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('mass_text') && (
              <Link className="nav-link" to="/mass-text">
                <i className="bi bi-chat-left-text me-2"></i>
                {!isSidebarCollapsed && <span>Mass Text</span>}
              </Link>
            )}

            {/* Client Verification - requires 'client_verification' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('client_verification') && (
              <Link className="nav-link" to="/client-verification">
                <i className="bi bi-shield-check me-2"></i>
                {!isSidebarCollapsed && <span>Client Verification</span>}
              </Link>
            )}

            {/* X Search - requires 'x_admin' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('x_admin') && (
              <Link className="nav-link" to="/x-search">
                <i className="bi bi-twitter-x me-2"></i>
                {!isSidebarCollapsed && <span>X Search</span>}
              </Link>
            )}

            {/* YouTube - requires 'youtube_admin' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('youtube_admin') && (
              <Link className="nav-link" to="/youtube">
                <i className="bi bi-youtube me-2"></i>
                {!isSidebarCollapsed && <span>YouTube</span>}
              </Link>
            )}

            {/* SEC Filings - requires 'sec_admin' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('sec_admin') && (
              <Link className="nav-link" to="/sec-filings">
                <i className="bi bi-file-earmark-text me-2"></i>
                {!isSidebarCollapsed && <span>SEC Filings</span>}
              </Link>
            )}

            {/* Company Reviews - requires 'company_reviews' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('company_reviews') && (
              <Link className="nav-link" to="/company-reviews">
                <i className="bi bi-star-half me-2"></i>
                {!isSidebarCollapsed && <span>Company Reviews</span>}
              </Link>
            )}

            {/* Company Research - requires 'company_research' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('company_research') && (
              <Link className="nav-link" to="/company-research">
                <i className="bi bi-search me-2"></i>
                {!isSidebarCollapsed && <span>Company Research</span>}
              </Link>
            )}

            {/* Client Forms (Cashflow + FPR) - requires 'cashflow_admin' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('cashflow_admin') && (
              <Link className="nav-link" to="/client-forms">
                <i className="bi bi-clipboard-data me-2"></i>
                {!isSidebarCollapsed && <span>Client Forms</span>}
              </Link>
            )}

            {/* Redactor - requires 'redactor' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('redactor') && (
              <Link className="nav-link" to="/redactor">
                <i className="bi bi-shield-lock me-2"></i>
                {!isSidebarCollapsed && <span>The Redactor</span>}
              </Link>
            )}

            {/* AutoTrader - requires 'auto_trader' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('auto_trader') && (
              <Link className="nav-link" to="/auto-trader">
                <i className="bi bi-robot me-2"></i>
                {!isSidebarCollapsed && <span>AutoTrader</span>}
              </Link>
            )}

            {/* Prompt Manager - requires 'prompt_manager' or 'admin' permission */}
            {auth.user && auth.hasPermission && (auth.hasPermission('prompt_manager')) && (
              <Link className="nav-link" to="/prompt-manager">
                <i className="bi bi-file-text me-2"></i>
                {!isSidebarCollapsed && <span>Prompt Manager</span>}
              </Link>
            )}

            {/* Resources - requires 'resources' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('resources') && (
              <Link className="nav-link" to="/resources">
                <i className="bi bi-database me-2"></i>
                {!isSidebarCollapsed && <span>Resources Helper</span>}
              </Link>
            )}

            <Link
              className="nav-link"
              to="/help"
              title={unseenReleaseNotes ? "Help & What's New (new release notes)" : "Help & What's New"}
            >
              <i className="bi bi-question-circle me-2"></i>
              {!isSidebarCollapsed && <span>Help &amp; What's New</span>}
              {unseenReleaseNotes && <sup className="help-unseen-dot" data-testid="help-unseen-dot" aria-hidden="true"></sup>}
            </Link>

            <Link className="nav-link" to="/settings">
              <i className="bi bi-gear me-2"></i>
              {!isSidebarCollapsed && <span>Settings</span>}
            </Link>

            {/* User Management - requires 'account_admin' permission */}
            {auth.user && auth.hasPermission && auth.hasPermission('account_admin') && (
              <Link className="nav-link" to="/user-management">
                <i className="bi bi-people me-2"></i>
                {!isSidebarCollapsed && <span>User Management</span>}
              </Link>
            )}
            <div className="border-top border-secondary my-3"></div>
            <button
              className="nav-link text-danger border-0 bg-transparent text-start"
              onClick={handleLogout}
              style={{ cursor: 'pointer' }}
            >
              <i className="bi bi-box-arrow-right me-2"></i>
              {!isSidebarCollapsed && <span>Log Off</span>}
            </button>
          </nav>
        </div>

        {/* Main Content */}
        <main className={`main-content ${isSidebarCollapsed ? 'expanded' : ''} ${fullBleed ? 'main-content--full-bleed' : ''}`}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
